import {
  Checkbox,
  ControlledDropDown,
  CustomRadioGroup,
  DropdownType,
  FileInput,
  FormInput,
  ToggleButton,
  MultiLineInput,
  SSNInput,
  PhoneInput,
  ControlledMultiSelect,
  CurrencyInput,
  FormDateInput,
  DATE_FIELD_TYPES,
  ZipInput,
  NAMESPACE,
} from '@ui-kit'
import {
  FieldValues,
  UseFormRegister,
  Path,
  FieldErrors,
  RegisterOptions,
  DeepRequired,
  PathValue,
  useWatch,
} from 'react-hook-form'
import {
  elementHasPermission,
  getDefaultValueFromMeta,
  getDisabledFromElement,
  getValidationsFromElement,
  isAuditField,
} from '../utils/form.utils'
import {
  componentNames,
  DATE_RESTRICTION_TYPES,
  DECIMAL_REGEX,
  EMAIL_REGEX,
  ENTITY_NAME,
  FIELD_VALUE_CONSTANTS,
  FILE_INPUT_TYPES,
  FULL_URL_REGEX,
  NAMESPACES,
  NUMERIC_REGEX,
  PHONE_REGEX,
  premitiveDataTypes,
  ZIPCODE_REGEX,
} from '../utils/constants'
import { ElementRendererProps } from '../types/formRenderer.types'
import LookupRender from './LookupRender'
import MultiLookupRender from './MultiLookupRender'
import NPIRender from './common/NPIRender'
import DEARender from './common/DEARender'
import { STRINGS } from '../utils/strings'
import MultiUploadWrapper from './MultiUploadWrapper'
import LookupCombobox from './LookupCombobox'
import CategoryInput from './common/CategoryInput'
import {
  addToaster,
  clearEntityDataForUsResident,
  LayoutResponseType,
  selectMergedBehaviorObj,
} from '@app-store'
import ComputeInput from './common/ComputeInput'
import { useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '../utils/agent.store'
import { uniqueId } from 'lodash'
import { ToasterTypeValues } from '../types/common'
import { useFormBehaviorState } from '../form-behavior/useFormBehaviorState'
import {
  getEffectiveRequired,
  getEffectiveDisabled,
  getEffectiveDateBound,
} from '../form-behavior/formBehavior.utils'
import { getFormBehaviorRuleKey } from '../form-behavior/useFormBehaviorFieldOverrides'
import { triggerFormBehaviorEvaluation } from '../form-behavior/formBehaviorTrigger.registry'

const ComponentsTypeA: string[] = [
  componentNames.NUMBER,
  componentNames.SINGLE_LINE,
  componentNames.MULTI_LINE,
  componentNames.CHECKBOX,
  componentNames.RADIO_GROUP,
  componentNames.DATE,
  componentNames.DATE_TIME,
  componentNames.TIME,
  componentNames.TOGGLE_SWITCH,
  componentNames.DROPDOWN,
  componentNames.CATEGORY,
  componentNames.COMPUTED,
  componentNames.HEADER,
  componentNames.PARAGRAPH,
  componentNames.DECIMAL,
  componentNames.ADDRESS,
  componentNames.NAME,
]
const ComponentsTypeB: string[] = [
  componentNames.PHONE_NUMBER,
  componentNames.EMAIL,
  componentNames.FILE_UPLOAD,
  componentNames.IMAGE_UPLOAD,
  componentNames.AUDIO_VIDEO_UPLOAD,
  componentNames.UNIQUE_ID,
  componentNames.DESCRIPTION,
  componentNames.SLIDER,
  componentNames.RATING,
  componentNames.WEBSITE,
  componentNames.URL,
  componentNames.SSN,
  componentNames.CURRENCY,
  componentNames.SINGLE_LOOKUP,
  componentNames.LINKED,
  componentNames.COMBOBOX,
  componentNames.MULTI_LOOKUP,
  componentNames.NPI,
  componentNames.DEA,
  componentNames.MULTI_SELECT_DROPDOWN,
  componentNames?.FAX_NUMBER,
  componentNames?.ZIP_CODE,
]
const RenderCompnentsTypeA = <T extends FieldValues>({
  element,
  register,
  errors,
  control,
  setValue,
  mode,
  fullLayout,
  ...rest
}: ElementRendererProps<T>) => {
  const layoutUid = fullLayout?.uid
  const hasFormRules =
    !!layoutUid && (fullLayout?.layout_data?.meta?.form_rules?.length ?? 0) > 0
  const formBehavior = useFormBehaviorState(layoutUid)
  const hasPermission = elementHasPermission(element, mode)
  const isDisabled =
    !hasPermission || getDisabledFromElement(element) || isAuditField(element)
  const effectiveRequired = getEffectiveRequired(
    element.keyName ?? '',
    element.meta.required,
    formBehavior.requiredOverrides
  )
  const effectiveDisabled = getEffectiveDisabled(
    element.keyName ?? '',
    isDisabled,
    formBehavior.disabledOverrides
  )
  const us_resident_data = useAppSelector(
    (state) => state?.updatedEntityData?.entityData
  )
  const dispatch = useAppDispatch()
  useEffect(() => {
    if (!us_resident_data || element?.keyName !== 'us_resident') return

    const fieldKey = `us_resident${rest?.dataId ?? ''}`
    const value = us_resident_data[fieldKey]
    if (!value && value !== '') return

    setValue(
      element.keyName as Path<T>,
      { id: value } as PathValue<T, Path<T>>,
      { shouldValidate: true }
    )

    dispatch(clearEntityDataForUsResident())
  }, [us_resident_data])

  const setupDateRange = () => {
    const today = new Date().toISOString().split('T')[0]
    const dateRange: { minDate: string; maxDate: string } = {
      minDate: '',
      maxDate: '',
    }
    if (element?.meta?.control_values?.[0] === DATE_RESTRICTION_TYPES?.FUTURE) {
      dateRange.maxDate = today
    } else if (
      element?.meta?.control_values?.[0] === DATE_RESTRICTION_TYPES?.PAST
    ) {
      dateRange.minDate = today
    }
    return dateRange
  }
  const dateRange = setupDateRange()
  const ruleKey = getFormBehaviorRuleKey(element)
  const effectiveMinDate = getEffectiveDateBound(
    ruleKey,
    dateRange.minDate,
    formBehavior.minDateOverrides
  )
  const effectiveMaxDate = getEffectiveDateBound(
    ruleKey,
    dateRange.maxDate,
    formBehavior.maxDateOverrides
  )
  const entityName = fullLayout?.entity_name ?? ''

  const triggerFieldFormBehaviorChange = () => {
    if (hasFormRules && layoutUid && element.keyName) {
      triggerFormBehaviorEvaluation(layoutUid, 'change', element.keyName)
    }
  }

  const registerWithFormBehavior: UseFormRegister<T> = (name, options) => {
    const registration = register(name, options)
    if (name !== element.keyName) {
      return registration
    }
    return {
      ...registration,
      onChange: (event) => {
        const result = registration.onChange(event)
        triggerFieldFormBehaviorChange()
        return result
      },
    }
  }

  switch (element.elementName) {
    case componentNames.NUMBER:
      return (
        <FormInput
          tabIndex={element.tabIndex}
          register={registerWithFormBehavior}
          name={element.keyName as Path<T>}
          errors={errors}
          validation={{
            ...getValidationsFromElement(element),
            pattern:
              element?.meta?.control_type === premitiveDataTypes?.float?.value
                ? {
                    value: DECIMAL_REGEX,
                    message: NAMESPACES?.INCORRECT_DEFAULT_VALUE,
                  }
                : {
                    value: NUMERIC_REGEX,
                    message: NAMESPACES?.INCORRECT_DEFAULT_VALUE,
                  },
          }}
          onKeyDown={(evt) =>
            ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()
          }
          defaultValue={getDefaultValueFromMeta(element.meta)}
          label={element.meta.label}
          placeholder={element.meta.placeholder}
          disabled={isDisabled}
          hasPermission={hasPermission}
          controlType={element.meta.control_type}
          dataQaId={element.keyName ?? ''}
          type="number"
        />
      )
    case componentNames.SINGLE_LINE:
      return (
        <FormInput
          register={registerWithFormBehavior}
          tabIndex={element.tabIndex}
          name={element.keyName as Path<T>}
          errors={errors}
          validation={
            getValidationsFromElement(element) as
              | RegisterOptions<T, Path<T>>
              | undefined
          }
          defaultValue={getDefaultValueFromMeta(element.meta)}
          label={element.meta.label}
          placeholder={element.meta.placeholder}
          disabled={isDisabled}
          hasPermission={hasPermission}
          dataQaId={element.keyName ?? ''}
        />
      )
    case componentNames.MULTI_LINE:
      return (
        <MultiLineInput
          tabIndex={element.tabIndex}
          register={register}
          name={element.keyName as Path<T>}
          errors={errors}
          validation={
            getValidationsFromElement(element) as
              | RegisterOptions<T, Path<T>>
              | undefined
          }
          defaultValue={getDefaultValueFromMeta(element.meta)}
          label={element.meta.label}
          placeholder={element.meta.placeholder}
          disabled={isDisabled}
          hasPermission={hasPermission}
          rows={FIELD_VALUE_CONSTANTS.TWO}
          dataQaId={element.keyName ?? ''}
        />
      )
    case componentNames.CHECKBOX:
      return (
        <div className="min-h-9">
          <Checkbox
            tabIndex={element.tabIndex}
            name={element.keyName as Path<DeepRequired<T>['toString']>}
            register={
              register as unknown as UseFormRegister<
                DeepRequired<T>['toString']
              >
            }
            errors={errors as FieldErrors<DeepRequired<T>['toString']>}
            label={element.meta.label}
            validation={
              getValidationsFromElement(element) as
                | RegisterOptions<
                    DeepRequired<T>['toString'],
                    Path<DeepRequired<T>['toString']>
                  >
                | undefined
            }
            defaultValue={getDefaultValueFromMeta(element.meta)}
            disabled={isDisabled}
            dataQaId={element.keyName ?? ''}
          />
        </div>
      )
    case componentNames.RADIO_GROUP:
      return (
        <div>
          <div className="font-primary font-light">
            {element?.meta?.label}
            {element.meta.required ? (
              <span
                className={`ml-1 text-xs leading-1 font-extraLight  ${
                  isDisabled ? 'text-accent-300' : 'text-error-500'
                }`}
              >
                *
              </span>
            ) : null}
          </div>
          <CustomRadioGroup
            tabIndex={element.tabIndex}
            // label={element.meta.label}
            options={element.meta.options ?? []}
            register={register}
            flexDirection={'flex flex-wrap'}
            name={element.keyName as Path<T>}
            disabled={isDisabled}
            defaultValue={getDefaultValueFromMeta(element.meta)}
            errors={errors as unknown as FieldErrors<FieldValues>}
            validation={
              getValidationsFromElement(element) as
                | RegisterOptions<T, Path<T>>
                | undefined
            }
            labelClasses="w-full block"
            dataQaId={element.keyName ?? ''}
          />
        </div>
      )
    case componentNames.DATE:
      return (
        <FormDateInput
          control={control}
          tabIndex={element.tabIndex}
          name={element.keyName as Path<T>}
          handleChange={(date?: string | null) => {
            setValue(element.keyName as Path<T>, date as PathValue<T, Path<T>>)
            if (hasFormRules && layoutUid) {
              triggerFormBehaviorEvaluation(
                layoutUid,
                'change',
                element.keyName
              )
            }
          }}
          defaultValue={getDefaultValueFromMeta(element.meta) as string}
          label={element.meta.label}
          placeholder={element.meta.placeholder}
          isDisabled={effectiveDisabled}
          hasPermission={hasPermission}
          min={effectiveMinDate}
          max={effectiveMaxDate}
          dataQaId={element.keyName ?? ''}
          required={effectiveRequired}
          type={DATE_FIELD_TYPES.DATE_TYPE}
        />
      )
    case componentNames.DATE_TIME:
      return (
        <FormDateInput
          control={control}
          tabIndex={element.tabIndex}
          name={element.keyName as Path<T>}
          handleChange={(date?: string | null) => {
            setValue(element.keyName as Path<T>, date as PathValue<T, Path<T>>)
            if (hasFormRules && layoutUid) {
              triggerFormBehaviorEvaluation(
                layoutUid,
                'change',
                element.keyName
              )
            }
          }}
          defaultValue={getDefaultValueFromMeta(element.meta) as string}
          label={element.meta.label}
          placeholder={element.meta.placeholder}
          isDisabled={effectiveDisabled}
          hasPermission={hasPermission}
          min={effectiveMinDate}
          max={effectiveMaxDate}
          required={effectiveRequired}
          dataQaId={element.keyName ?? ''}
          type={DATE_FIELD_TYPES.DATE_TIME_LOCAL}
        />
      )
    case componentNames.TIME:
      return (
        <FormInput
          tabIndex={element.tabIndex}
          type={FILE_INPUT_TYPES.TIME}
          register={register}
          onChange={(e) => {
            const inputValue = e.target.value
            setValue(
              element.keyName as Path<T>,
              `${inputValue}:00` as PathValue<T, Path<T>>
            )
            if (hasFormRules && layoutUid) {
              triggerFormBehaviorEvaluation(
                layoutUid,
                'change',
                element.keyName
              )
            }
          }}
          name={element.keyName as Path<T>}
          errors={errors}
          validation={
            getValidationsFromElement(element) as
              | RegisterOptions<T, Path<T>>
              | undefined
          }
          defaultValue={getDefaultValueFromMeta(element.meta)}
          label={element.meta.label}
          placeholder={element.meta.placeholder}
          disabled={isDisabled}
          hasPermission={hasPermission}
          dataQaId={element.keyName ?? ''}
        />
      )
    case componentNames.TOGGLE_SWITCH:
      return (
        <ToggleButton
          tabIndex={element.tabIndex}
          name={element.keyName as Path<DeepRequired<T>['toString']>}
          register={
            register as unknown as UseFormRegister<DeepRequired<T>['toString']>
          }
          errors={errors as unknown as FieldErrors<DeepRequired<T>['toString']>}
          validation={
            getValidationsFromElement(element) as
              | RegisterOptions<
                  DeepRequired<T>['toString'],
                  Path<DeepRequired<T>['toString']>
                >
              | undefined
          }
          disabled={isDisabled}
          dataQaId={element.keyName ?? ''}
        />
      )

    case componentNames.DROPDOWN:
      return (
        <ControlledDropDown
          tabIndex={element.tabIndex}
          name={element.keyName as Path<T>}
          errors={errors}
          label={element.meta.label}
          control={control}
          isSearchable={true}
          onChange={(e: DropdownType<string>) => {
            setValue(
              element.keyName as Path<T>,
              e?.value as PathValue<T, Path<T>>
            )
            if (hasFormRules && layoutUid) {
              triggerFormBehaviorEvaluation(
                layoutUid,
                'change',
                element.keyName
              )
            }
          }}
          options={element.meta.options || []}
          required={effectiveRequired}
          disabled={isDisabled}
          hasPermission={hasPermission}
          isCrossable
          dataQaId={element.keyName ?? ''}
          customValidation={(value, options, required) => {
            if (required) {
              const defaultOptionsExists =
                element?.keyName !== NAMESPACES.RECORD_STATUS_LOWERCASE ||
                options.some((opt) => opt.value === value)
              return defaultOptionsExists || NAMESPACES.REQUIRED_FIELD
            }
            return true
          }}
        />
      )

    case componentNames.CATEGORY:
      return (
        <CategoryInput
          tabIndex={element.tabIndex}
          name={element.keyName as Path<T>}
          errors={errors}
          label={element.meta.label}
          control={control}
          isSearchable={true}
          onChange={(e: DropdownType<string>) => {
            setValue(
              element.keyName as Path<T>,
              e?.value as PathValue<T, Path<T>>
            )
            if (hasFormRules && layoutUid) {
              triggerFormBehaviorEvaluation(
                layoutUid,
                'change',
                element.keyName
              )
            }
          }}
          setValue={setValue}
          required={effectiveRequired}
          disabled={isDisabled}
          hasPermission={hasPermission}
          categoryKey={element.meta.filter_criteria?.category_type ?? ''}
          allowedCategoryIds={
            hasFormRules &&
            Object.prototype.hasOwnProperty.call(
              formBehavior.categoryAllowedIds,
              element.keyName ?? ''
            )
              ? formBehavior.categoryAllowedIds[element.keyName ?? '']
              : undefined
          }
          searchLength={
            [
              ENTITY_NAME.PATIENTS,
              ENTITY_NAME.PROVDERS,
              ENTITY_NAME.CONTACTS,
            ]?.includes(entityName) && element?.keyName === NAMESPACES.SUFFIX
              ? FIELD_VALUE_CONSTANTS.ONE
              : FIELD_VALUE_CONSTANTS.THREE
          }
          dataQaId={element.keyName ?? ''}
        />
      )

    case componentNames.COMPUTED:
      return (
        <ComputeInput
          tabIndex={element?.tabIndex}
          name={element?.keyName as Path<T>}
          errors={errors}
          label={element?.meta?.label}
          register={register}
          formula={element?.meta?.formula}
          control={control}
          setValue={setValue}
          dataQaId={element.keyName ?? ''}
        />
      )

    case componentNames.HEADER:
      return (
        <div className="text-base font-medium font-primary min-h-9">
          <div
            data-qa-id={
              element?.keyName || `header-${fullLayout?.id}-${element?.id}`
            }
            className="text-base font-medium font-primary min-h-9"
          >
            {element.meta.label}
          </div>
        </div>
      )
    case componentNames.PARAGRAPH:
      return (
        <div
          data-qa-id={
            element?.keyName || `paragraph-${fullLayout?.id}-${element?.id}`
          }
          className="text-large font-primary font-light min-h-9"
        >
          {element.meta.label}
        </div>
      )
    case componentNames.DECIMAL:
      return (
        <FormInput
          tabIndex={element.tabIndex}
          type={FILE_INPUT_TYPES.NUMBER}
          register={register}
          name={element.keyName as Path<T>}
          errors={errors}
          validation={
            getValidationsFromElement(element) as
              | RegisterOptions<T, Path<T>>
              | undefined
          }
          defaultValue={getDefaultValueFromMeta(element.meta)}
          label={element.meta.label}
          placeholder={element.meta.placeholder}
          disabled={isDisabled}
          hasPermission={hasPermission}
          dataQaId={element.keyName ?? ''}
        />
      )
    case componentNames.ADDRESS:
    case componentNames.NAME:
      return (
        <FormInput
          tabIndex={element.tabIndex}
          register={register}
          name={element.keyName as Path<T>}
          errors={errors}
          validation={
            getValidationsFromElement(element) as
              | RegisterOptions<T, Path<T>>
              | undefined
          }
          defaultValue={getDefaultValueFromMeta(element.meta)}
          label={element.meta.label}
          placeholder={element.meta.placeholder}
          disabled={isDisabled}
          hasPermission={hasPermission}
          dataQaId={element.keyName ?? ''}
        />
      )
  }
}
const RenderCompnentsTypeB = <T extends FieldValues>({
  data,
  element,
  register,
  errors,
  control,
  setValue,
  clearErrors,
  mode,
  formUniqueId = '',
  fullLayout,
  dataId,
  subFormMetaObject,
  fromIntakeReview,
  getValues,
}: ElementRendererProps<T>) => {
  const layoutUid = fullLayout?.uid
  const hasFormRules =
    !!layoutUid && (fullLayout?.layout_data?.meta?.form_rules?.length ?? 0) > 0
  const formBehavior = useFormBehaviorState(layoutUid)
  const effectiveRequired = getEffectiveRequired(
    element.keyName ?? '',
    element.meta.required,
    formBehavior.requiredOverrides
  )
  const hasPermission = elementHasPermission(element, mode)
  const isDisabled =
    !hasPermission || getDisabledFromElement(element) || isAuditField(element)
  const effectiveDisabled = getEffectiveDisabled(
    element.keyName ?? '',
    isDisabled,
    formBehavior.disabledOverrides
  )
  const valueOfThisElement =
    useWatch({ name: element.keyName }) ?? data?.[element?.keyName]?.id
  const dispatch = useAppDispatch()
  const handleToaster = (message: string, type: ToasterTypeValues) => {
    if (message === NAMESPACE.INVALID_FILE_SIGNATURE_TOASTER_MSG) {
      dispatch(
        addToaster({
          id: uniqueId(),
          message: message,
          type: type,
        })
      )
    }
  }
  switch (element.elementName) {
    case componentNames.PHONE_NUMBER:
      return (
        <PhoneInput
          tabIndex={element.tabIndex}
          register={register}
          name={element.keyName as Path<T>}
          errors={errors}
          validation={
            {
              ...getValidationsFromElement(element),
              pattern: {
                value: PHONE_REGEX,
                message: NAMESPACES.PHONE_VALIDATION_MSG,
              },
            } as RegisterOptions<T, Path<T>> | undefined
          }
          defaultValue={getDefaultValueFromMeta(element.meta)}
          label={element.meta.label}
          placeholder={element.meta.placeholder}
          disabled={isDisabled}
          hasPermission={hasPermission}
          setValue={setValue}
          control={control}
          dataQaId={element.keyName ?? ''}
        />
      )
    case componentNames.ZIP_CODE:
      return (
        <ZipInput
          tabIndex={element.tabIndex}
          register={register}
          name={element.keyName as Path<T>}
          errors={errors}
          validation={
            {
              ...getValidationsFromElement(element),
              pattern: {
                value: ZIPCODE_REGEX,
                message: NAMESPACES.ZIPCODE_VALIDATION_MSG,
              },
            } as RegisterOptions<T, Path<T>> | undefined
          }
          defaultValue={getDefaultValueFromMeta(element.meta)}
          label={element.meta.label}
          placeholder={element.meta.placeholder}
          disabled={isDisabled}
          hasPermission={hasPermission}
          setValue={setValue}
          control={control}
          dataQaId={element.keyName ?? ''}
        />
      )
    case componentNames?.FAX_NUMBER:
      return (
        <PhoneInput
          tabIndex={element?.tabIndex}
          register={register}
          name={element?.keyName as Path<T>}
          errors={errors}
          validation={
            {
              ...getValidationsFromElement(element),
              pattern: {
                value: PHONE_REGEX,
                message: NAMESPACES?.PHONE_VALIDATION_MSG,
              },
            } as RegisterOptions<T, Path<T>>
          }
          defaultValue={getDefaultValueFromMeta(element.meta)}
          label={element.meta.label}
          placeholder={element.meta.placeholder}
          disabled={isDisabled}
          hasPermission={hasPermission}
          setValue={setValue}
          control={control}
          dataQaId={element.keyName ?? ''}
        />
      )
    case componentNames.EMAIL:
      return (
        <FormInput
          tabIndex={element.tabIndex}
          register={register}
          name={element.keyName as Path<T>}
          errors={errors}
          validation={
            {
              ...getValidationsFromElement(element),
              pattern: {
                value: EMAIL_REGEX,
                message: NAMESPACES.EMAIL_VALIDATION_MSG,
              },
            } as RegisterOptions<T, Path<T>> | undefined
          }
          defaultValue={getDefaultValueFromMeta(element.meta)}
          label={element.meta.label}
          placeholder={element.meta.placeholder}
          disabled={isDisabled}
          hasPermission={hasPermission}
          dataQaId={element.keyName ?? ''}
        />
      )
    case componentNames.FILE_UPLOAD:
      return (
        <MultiUploadWrapper
          tabIndex={element.tabIndex}
          id={valueOfThisElement?.id}
          element={element}
          register={register}
          setValue={setValue}
          mode={mode}
          fromIntakeReview={fromIntakeReview}
        />
      )
    case componentNames.IMAGE_UPLOAD:
      return (
        <FileInput
          tabIndex={element.tabIndex}
          register={register}
          name={element.keyName as Path<T>}
          errors={errors}
          label={NAMESPACES.FILE}
          validation={
            getValidationsFromElement(element) as
              | RegisterOptions<T, Path<T>>
              | undefined
          }
          comment={STRINGS.MAX_SIZE_10_MB}
          commentLabel={STRINGS.JPG_PNG}
          disabled={isDisabled}
          dataQaId={element.keyName ?? ''}
          handleToaster={handleToaster}
        />
      )
    case componentNames.AUDIO_VIDEO_UPLOAD:
      return (
        <FileInput
          tabIndex={element.tabIndex}
          register={register}
          name={element.keyName as Path<T>}
          errors={errors}
          label={NAMESPACES.FILE}
          validation={
            getValidationsFromElement(element) as
              | RegisterOptions<T, Path<T>>
              | undefined
          }
          comment={STRINGS.MAX_SIZE_20_MB}
          commentLabel={STRINGS.MP3_MP4}
          disabled={isDisabled}
          dataQaId={element.keyName ?? ''}
          handleToaster={handleToaster}
        />
      )
    case componentNames.UNIQUE_ID:
      return (
        <FormInput
          tabIndex={element.tabIndex}
          register={register}
          name={element.keyName as Path<T>}
          errors={errors}
          defaultValue={getDefaultValueFromMeta(element.meta)}
          label={element.meta.label}
          placeholder={element.meta.placeholder}
          disabled={true}
          dataQaId={element.keyName ?? ''}
        />
      )
    case componentNames.DESCRIPTION:
    case componentNames.SLIDER:
    case componentNames.RATING:
      return

    case componentNames.WEBSITE:
    case componentNames.URL:
      return (
        <FormInput
          tabIndex={element.tabIndex}
          register={register}
          name={element.keyName as Path<T>}
          errors={errors}
          validation={
            {
              ...getValidationsFromElement(element),
              pattern: {
                value: FULL_URL_REGEX,
                message: NAMESPACES.WEBSITE_VALIDATION_MSG,
              },
            } as RegisterOptions<T, Path<T>> | undefined
          }
          onChange={(e) => {
            const newVal = e.target.value.trim() as PathValue<T, Path<T>>
            if (e.target.value !== newVal)
              setValue(element.keyName as Path<T>, newVal)
            if (hasFormRules && layoutUid) {
              triggerFormBehaviorEvaluation(
                layoutUid,
                'change',
                element.keyName
              )
            }
          }}
          defaultValue={getDefaultValueFromMeta(element.meta)}
          label={element.meta.label}
          placeholder={element.meta.placeholder}
          disabled={isDisabled}
          hasPermission={hasPermission}
          dataQaId={element.keyName ?? ''}
        />
      )

    case componentNames.SSN:
      return (
        <SSNInput
          tabIndex={element.tabIndex}
          register={register}
          name={element.keyName as Path<T>}
          errors={errors}
          defaultValue={getDefaultValueFromMeta(element.meta)}
          label={element.meta.label}
          placeholder={element.meta.placeholder}
          validation={getValidationsFromElement(element)}
          disabled={isDisabled}
          hasPermission={hasPermission}
          setValue={setValue}
          dataQaId={element.keyName ?? ''}
          maxLength={
            element?.meta?.char_limit
              ? Number(element?.meta?.char_limit)
              : undefined
          }
        />
      )

    case componentNames.CURRENCY:
      return (
        <CurrencyInput
          tabIndex={element.tabIndex}
          register={register}
          name={element.keyName as Path<T>}
          errors={errors}
          validation={
            {
              ...getValidationsFromElement(element),
            } as RegisterOptions<T, Path<T>> | undefined
          }
          defaultValue={getDefaultValueFromMeta(element.meta)}
          label={element.meta.label}
          placeholder={element.meta.placeholder}
          disabled={isDisabled}
          hasPermission={hasPermission}
          setValue={setValue}
          dataQaId={element.keyName ?? ''}
        />
      )

    case componentNames.SINGLE_LOOKUP:
      return (
        <LookupRender
          tabIndex={element.tabIndex}
          element={element}
          errors={errors}
          control={control}
          setValue={setValue}
          required={effectiveRequired}
          // validation={getValidationsFromElement(element)}
          disabled={effectiveDisabled}
          formUniqueId={formUniqueId}
          fullLayout={fullLayout as LayoutResponseType}
          dataId={dataId}
          subFormMetaObject={subFormMetaObject}
          mode={mode}
          dataQaId={element.keyName ?? ''}
          defaultValue={getDefaultValueFromMeta(element.meta) as string}
          getValues={getValues}
          behaviorLookupAllowedIds={
            hasFormRules &&
            Object.prototype.hasOwnProperty.call(
              formBehavior.lookupAllowedIds,
              element.keyName ?? ''
            )
              ? formBehavior.lookupAllowedIds[element.keyName ?? '']
              : undefined
          }
        />
      )
    case componentNames.LINKED:
      return (
        <LookupRender
          tabIndex={element.tabIndex}
          element={element}
          errors={errors}
          control={control}
          setValue={setValue}
          required={effectiveRequired}
          // validation={getValidationsFromElement(element)}
          disabled={effectiveDisabled}
          formUniqueId={formUniqueId}
          fullLayout={fullLayout as LayoutResponseType}
          dataId={dataId}
          subFormMetaObject={subFormMetaObject}
          mode={mode}
          dataQaId={element.keyName ?? ''}
          defaultValue={getDefaultValueFromMeta(element.meta) as string}
          getValues={getValues}
          behaviorLookupAllowedIds={
            hasFormRules &&
            Object.prototype.hasOwnProperty.call(
              formBehavior.lookupAllowedIds,
              element.keyName ?? ''
            )
              ? formBehavior.lookupAllowedIds[element.keyName ?? '']
              : undefined
          }
        />
      )
    case componentNames.COMBOBOX:
      return (
        <LookupCombobox
          tabIndex={element.tabIndex}
          element={element}
          errors={errors}
          control={control}
          setValue={setValue}
          required={effectiveRequired}
          // validation={getValidationsFromElement(element)}
          disabled={isDisabled}
          dataQaId={element.keyName ?? ''}
        />
      )
    case componentNames.MULTI_LOOKUP:
      return (
        <MultiLookupRender
          element={element}
          errors={errors}
          control={control}
          setValue={setValue}
          required={effectiveRequired}
          // validation={getValidationsFromElement(element)}
          disabled={isDisabled}
          dataQaId={element.keyName ?? ''}
        />
      )
    case componentNames.NPI:
      return (
        <NPIRender
          tabIndex={element.tabIndex}
          register={register}
          name={element.keyName as Path<T>}
          errors={errors}
          defaultValue={getDefaultValueFromMeta(element.meta)}
          label={element.meta.label}
          placeholder={element.meta.placeholder}
          validation={getValidationsFromElement(element)}
          clearErrors={clearErrors}
          disabled={isDisabled}
          hasPermission={hasPermission}
          dataQaId={element.keyName ?? ''}
        />
      )
    case componentNames.DEA:
      return (
        <DEARender
          tabIndex={element.tabIndex}
          register={register}
          name={element.keyName as Path<T>}
          depFieldkeyName={element.meta.selected_fields?.[1]?.name as Path<T>}
          errors={errors}
          defaultValue={getDefaultValueFromMeta(element.meta)}
          label={element.meta.label}
          placeholder={element.meta.placeholder}
          validation={getValidationsFromElement(element)}
          clearErrors={clearErrors}
          disabled={isDisabled}
          hasPermission={hasPermission}
          dataQaId={element.keyName ?? ''}
        />
      )
    case componentNames.MULTI_SELECT_DROPDOWN:
      return (
        <ControlledMultiSelect
          tabIndex={element.tabIndex}
          control={control}
          name={element.keyName as Path<T>}
          label={element.meta.label}
          errors={errors as unknown as FieldErrors<FieldValues>}
          options={element.meta.options || []}
          disabled={isDisabled}
          onChange={(e: DropdownType<string>[]) => {
            const valuesArray = e.map((item) => item.value)
            setValue(
              element.keyName as Path<T>,
              valuesArray as PathValue<T, Path<T>>
            )
            if (hasFormRules && layoutUid) {
              triggerFormBehaviorEvaluation(
                layoutUid,
                'change',
                element.keyName
              )
            }
          }}
          hasPermission={hasPermission}
          required={element.meta.required ?? false}
          dataQaId={element.keyName ?? ''}
        />
      )
    // Need for future reference:
    // return (
    //   <MultiSelectSearchDropDown
    //     options={element.meta.options || []}
    //     name={element.keyName as Path<T>}
    //     errors={errors as unknown as FieldErrors<FieldValues>}
    //     // defaultValue={getDefaultValueFromMeta(element.meta)}
    //     label={element.meta.label}
    //     // placeholder={element.meta.placeholder}
    //     // validation={getValidationsFromElement(element)}
    //     // clearErrors={clearErrors}
    //     disabled={getDisabledFromElement(element, mode)}
    //   />
    // )
  }
}
const ElementRenderer = <T extends FieldValues>({
  data,
  element,
  register,
  errors,
  control,
  setValue,
  clearErrors,
  mode,
  formUniqueId = '',
  fullLayout,
  dataId,
  subFormMetaObject,
  fromIntakeReview,
  getValues,
}: ElementRendererProps<T>) => {
  const behaviorObj = useAppSelector(selectMergedBehaviorObj)

  const effectiveElement = behaviorObj.disabled?.some(
    (key) => element.keyName === key || element.keyName?.endsWith(`.${key}`)
  )
    ? { ...element, meta: { ...element.meta, disabled: true } }
    : element

  const propsVaue = {
    data,
    element: effectiveElement,
    register,
    errors,
    control,
    setValue,
    clearErrors,
    mode,
    formUniqueId,
    fullLayout,
    dataId,
    subFormMetaObject,
    fromIntakeReview,
    getValues,
  }

  return (
    <>
      {ComponentsTypeA.includes(effectiveElement?.elementName) ? (
        <RenderCompnentsTypeA {...propsVaue} />
      ) : null}
      {ComponentsTypeB.includes(effectiveElement?.elementName) ? (
        <RenderCompnentsTypeB {...propsVaue} />
      ) : null}
    </>
  )
}

export default ElementRenderer
